import React, { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { Camera, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SEGUNDOS = 3;

// Cámara del registro de llegada: cuenta regresiva de 3 s antes de tomar la foto para que el
// empleado se acomode y se vea el uniforme completo. La imagen se muestra completa (sin recortar).
// Resolución 640×480 (4:3, más alta que 16:9) para que entre más del cuerpo sin pasar el límite
// de tamaño del backend.
const FotoLlegada = ({ imgSrc, onCapture, onRetake, hora, disabled = false }) => {
  const webcamRef = useRef(null);
  const [cuenta, setCuenta] = useState(null); // null = sin cuenta regresiva
  const [errorCamara, setErrorCamara] = useState(null);

  useEffect(() => {
    if (cuenta == null) return undefined;
    if (cuenta === 0) {
      const foto = webcamRef.current?.getScreenshot();
      setCuenta(null);
      if (foto) onCapture(foto);
      return undefined;
    }
    const t = setTimeout(() => setCuenta((c) => (c == null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [cuenta, onCapture]);

  const iniciar = useCallback(() => setCuenta(SEGUNDOS), []);
  const cancelar = useCallback(() => setCuenta(null), []);

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-black flex items-center justify-center">
        {imgSrc ? (
          <img src={imgSrc} alt="Foto de llegada" className="w-full h-full object-contain" />
        ) : (
          <>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              screenshotQuality={0.65}
              forceScreenshotSourceSize
              videoConstraints={{ width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }}
              onUserMediaError={() => setErrorCamara('No se pudo acceder a la cámara. Revisa los permisos del navegador.')}
              className="w-full h-full object-contain"
            />
            {/* Guía de encuadre */}
            {cuenta == null && !errorCamara && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-center text-xs text-white">
                Ubícate a una distancia en la que se vea el uniforme completo
              </div>
            )}
            {cuenta != null && cuenta > 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25">
                <span key={cuenta} className="text-7xl font-bold text-white drop-shadow-lg animate-in zoom-in-50 duration-300">{cuenta}</span>
                <span className="mt-2 text-sm text-white drop-shadow">Acomódate…</span>
              </div>
            )}
            {errorCamara && (
              <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-white">{errorCamara}</div>
            )}
          </>
        )}
      </div>

      <div className="flex w-full items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Hora de registro</div>
          <div className="text-2xl font-bold text-primary tabular-nums">{hora}</div>
        </div>
        {imgSrc ? (
          <Button variant="outline" onClick={onRetake} disabled={disabled}>
            <RefreshCw className="mr-2 h-4 w-4" /> Repetir foto
          </Button>
        ) : cuenta != null ? (
          <Button variant="outline" onClick={cancelar}>
            <X className="mr-2 h-4 w-4" /> Cancelar ({cuenta})
          </Button>
        ) : (
          <Button onClick={iniciar} disabled={disabled || !!errorCamara}>
            <Camera className="mr-2 h-4 w-4" /> Tomar foto ({SEGUNDOS} s)
          </Button>
        )}
      </div>
    </div>
  );
};

export default FotoLlegada;
